import React, { useRef, useState } from "react";
import { userApi, apiError } from "@/lib/userApi";
import { toast } from "sonner";
import { Send, Paperclip, Smile, Mic, Square, X, Timer, Loader2 } from "lucide-react";
import { pulseField, stormSurge } from "@/lib/pulse";

const EMOJIS = ["😀", "😂", "🥳", "😍", "👍", "🙏", "🔥", "💪", "✅", "❌", "⚡", "💰",
  "📦", "🚚", "🤝", "👀", "😅", "😢", "🎉", "❤️", "🤔", "🙌", "📸", "🛒"];

/** Barra de escritura: texto, emojis, adjuntos, nota de voz y autodestrucción. */
export const Composer = ({ convId, replyTo, editing, onSent, onCancelReply, onCancelEdit, disabled }) => {
  const [text, setText] = useState(editing?.text || "");
  const [emoji, setEmoji] = useState(false);
  const [ttl, setTtl] = useState(0);
  const [busy, setBusy] = useState(false);
  const [rec, setRec] = useState(false);
  const fileRef = useRef(null);
  const recRef = useRef(null);
  const chunks = useRef([]);
  const lastTyping = useRef(0);

  const notifyTyping = () => {
    const now = Date.now();
    if (now - lastTyping.current < 2200) return;
    lastTyping.current = now;
    userApi.post(`/community/conversations/${convId}/typing`).catch(() => {});
  };

  const send = async (extra = {}) => {
    const body = { text: text.trim(), reply_to: replyTo?.msg_id || null, ...extra };
    if (!body.text && !body.media_id) return;
    if (ttl) body.ttl_seconds = ttl;
    setBusy(true);
    try {
      if (editing) {
        await userApi.patch(`/community/messages/${editing.msg_id}`, { text: body.text });
        onCancelEdit?.();
      } else {
        await userApi.post(`/community/conversations/${convId}/messages`, body);
      }
      setText(""); setEmoji(false); onCancelReply?.();
      stormSurge();
      onSent?.();
    } catch (e) {
      toast.error(apiError(e, "No se pudo enviar"));
    } finally { setBusy(false); }
  };

  const attach = async (file, kind) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await userApi.post("/community/media", fd);
      const auto = data.mime?.startsWith("image/") ? "image"
        : data.mime?.startsWith("video/") ? "video"
        : data.mime?.startsWith("audio/") ? "voice" : "file";
      await send({ media_id: data.media_id, kind: kind || auto });
    } catch (e) {
      toast.error(apiError(e, "No se pudo subir el archivo"));
    } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, MediaRecorder.isTypeSupported("audio/webm") ? { mimeType: "audio/webm" } : undefined);
      chunks.current = [];
      mr.ondataavailable = (e) => { if (e.data?.size) chunks.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks.current, { type: "audio/webm" });
        setRec(false);
        if (blob.size < 1200) return toast.error("No escuché nada, inténtalo otra vez");
        await attach(new File([blob], "nota-de-voz.webm", { type: "audio/webm" }), "voice");
      };
      mr.start(); recRef.current = mr; setRec(true);
    } catch {
      toast.error("No pude acceder al micrófono");
    }
  };

  return (
    <div className="border-t border-white/10 p-3" style={{ background: "rgba(10,3,4,0.5)" }}>
      {(replyTo || editing) && (
        <div className="flex items-center justify-between glass-card px-3 py-2 mb-2 text-xs text-white/70">
          <span className="truncate">
            {editing ? "Editando: " : "Respondiendo: "}{(editing || replyTo).text?.slice(0, 80) || "adjunto"}
          </span>
          <button data-testid="composer-cancel-context"
            onClick={() => { editing ? onCancelEdit?.() : onCancelReply?.(); setText(editing ? "" : text); }}>
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {emoji && (
        <div className="glass-card p-2 mb-2 grid grid-cols-12 gap-1" data-testid="emoji-palette">
          {EMOJIS.map((e) => (
            <button key={e} onClick={() => setText((t) => t + e)} className="text-lg hover:scale-125 transition-transform">{e}</button>
          ))}
        </div>
      )}

      <div className="flex items-end gap-2">
        <button data-testid="composer-emoji" onClick={() => setEmoji((v) => !v)}
          className="glass-btn w-10 h-10 rounded-xl flex items-center justify-center shrink-0">
          <Smile className="w-4 h-4" />
        </button>
        <button data-testid="composer-attach" onClick={() => fileRef.current?.click()} disabled={busy}
          className="glass-btn w-10 h-10 rounded-xl flex items-center justify-center shrink-0">
          <Paperclip className="w-4 h-4" />
        </button>
        <input ref={fileRef} type="file" className="hidden" data-testid="composer-file"
          onChange={(e) => attach(e.target.files?.[0])} />
        <button data-testid="composer-ttl" onClick={() => setTtl(ttl ? 0 : 3600)}
          title="Autodestrucción en 1 hora"
          className={`glass-btn ${ttl ? "glass-btn-red" : ""} w-10 h-10 rounded-xl flex items-center justify-center shrink-0`}>
          <Timer className="w-4 h-4" />
        </button>

        <textarea data-testid="composer-input" rows={1} value={text} disabled={disabled}
          onChange={(e) => { setText(e.target.value); notifyTyping(); }}
          onKeyDown={(e) => {
            pulseField(e.currentTarget);
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
          }}
          placeholder={disabled ? "Solo el administrador puede publicar aquí" : "Escribe un mensaje…"}
          className="glass-field flex-1 px-3.5 py-2.5 rounded-xl text-[15px] text-white placeholder-white/35 outline-none resize-none max-h-32" />

        {text.trim() || editing ? (
          <button data-testid="composer-send" onClick={() => send()} disabled={busy || disabled}
            className="glass-btn glass-btn-red w-10 h-10 rounded-xl flex items-center justify-center shrink-0">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        ) : (
          <button data-testid="composer-voice" onClick={() => (rec ? recRef.current?.stop() : startRec())}
            disabled={busy || disabled}
            className={`glass-btn ${rec ? "glass-btn-red" : ""} w-10 h-10 rounded-xl flex items-center justify-center shrink-0`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : rec ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </button>
        )}
      </div>
    </div>
  );
};

export default Composer;
